import React from 'react';
import { bool, func, oneOf, shape, string } from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { NamedLink } from '../../components';

import css from './ListingOwnerBanner.css';

export const OWNER_BANNER_DRAFT = 'draft';
export const OWNER_BANNER_PUBLISHED = 'published';
export const OWNER_BANNER_PENDING_APPROVAL = 'pendingApproval';
export const OWNER_BANNER_CLOSED = 'closed';

const TITLE_IDS = {
  [OWNER_BANNER_DRAFT]: 'ListingOwnerBanner.draftTitle',
  [OWNER_BANNER_PUBLISHED]: 'ListingOwnerBanner.publishedTitle',
  [OWNER_BANNER_PENDING_APPROVAL]: 'ListingOwnerBanner.pendingTitle',
  [OWNER_BANNER_CLOSED]: 'ListingOwnerBanner.closedTitle',
};

const TEXT_IDS = {
  [OWNER_BANNER_PUBLISHED]: 'ListingOwnerBanner.publishedText',
  [OWNER_BANNER_PENDING_APPROVAL]: 'ListingOwnerBanner.pendingText',
  [OWNER_BANNER_CLOSED]: 'ListingOwnerBanner.closedText',
};

// "a, b and c" in the locale's own words; plain commas without Intl.ListFormat.
const joinList = (items, locale) =>
  typeof Intl !== 'undefined' && typeof Intl.ListFormat === 'function'
    ? new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items)
    : items.join(', ');

const DraftText = ({ completeness, intl }) => {
  if (completeness.isComplete) {
    return <FormattedMessage id="ListingOwnerBanner.draftText" />;
  }
  const missingLabels = (completeness.missing || []).map(key =>
    intl.formatMessage({
      id: `ListingOwnerBanner.missing${key.charAt(0).toUpperCase()}${key.slice(1)}`,
    })
  );
  return (
    <FormattedMessage
      id="ListingOwnerBanner.missing"
      values={{ items: joinList(missingLabels, intl.locale) }}
    />
  );
};

// Sticky banner the owner sees on their own listing page, in every state.
// A draft also says what is still missing and offers Publish; the other
// states offer Edit only. Mirrors the PortfolioWorkPage draft banner.
const ListingOwnerBanner = props => {
  const {
    variant,
    completeness,
    onPublish,
    publishInProgress,
    publishError,
    editParams,
    intl,
  } = props;
  const isDraft = variant === OWNER_BANNER_DRAFT;

  return (
    <div className={css.root}>
      <div className={css.content}>
        <h3 className={css.title}>
          <FormattedMessage id={TITLE_IDS[variant]} />
        </h3>
        <p className={css.text}>
          {isDraft ? (
            <DraftText completeness={completeness} intl={intl} />
          ) : (
            <FormattedMessage id={TEXT_IDS[variant]} />
          )}
        </p>
        {isDraft && publishError ? (
          <p className={css.error}>
            <FormattedMessage id="ListingOwnerBanner.publishError" />
          </p>
        ) : null}
      </div>
      <div className={css.buttons}>
        <NamedLink name="EditListingPage" params={editParams} className={css.editButton}>
          <FormattedMessage id="ListingOwnerBanner.edit" />
        </NamedLink>
        {isDraft ? (
          <button
            type="button"
            className={css.publishButton}
            onClick={onPublish}
            disabled={!completeness.isComplete || publishInProgress}
          >
            <FormattedMessage id="ListingOwnerBanner.publish" />
          </button>
        ) : null}
      </div>
    </div>
  );
};

ListingOwnerBanner.defaultProps = {
  completeness: null,
  onPublish: null,
  publishInProgress: false,
  publishError: null,
};

ListingOwnerBanner.propTypes = {
  intl: intlShape.isRequired,
  variant: oneOf([
    OWNER_BANNER_DRAFT,
    OWNER_BANNER_PUBLISHED,
    OWNER_BANNER_PENDING_APPROVAL,
    OWNER_BANNER_CLOSED,
  ]).isRequired,
  // Draft only.
  completeness: shape({ isComplete: bool.isRequired }),
  onPublish: func,
  publishInProgress: bool,
  publishError: propTypes.error,
  editParams: shape({ id: string.isRequired, slug: string, type: string, tab: string }).isRequired,
};

export default injectIntl(ListingOwnerBanner);
